import React, { useEffect, useState } from "react";
import axios from "axios";
import { RSQLFilterExpression, Operators } from "rsql-criteria-typescript";

import { findView } from "api/entities";
import {
  getAutocompleteCache,
  getCacheKey,
  registerAutocompleteCallback,
  setAutocompleteCache,
} from "composants/autocomplete/autocomplete-cache";
import { ComponentState } from "types/Component";
import { Pojo } from "types/Galaxy";
import { initRsqlCriteria } from "utils/query.utils";

import { CustomCellRendererProps } from "ag-grid-react";

type AutocompleteViewEntity = { id: string; label: string };

function isEntityValue(value: unknown): value is AutocompleteViewEntity {
  return value !== null && typeof value === "object" && "id" in (value as object);
}

function toViewEntity(value: { id?: unknown; label?: unknown }): AutocompleteViewEntity {
  const id = String(value.id ?? "");
  return { id, label: String(value.label ?? id) };
}

function AutocompleteViewCellRendererContent({
  params,
  compo,
  sjmoCode,
}: {
  params: CustomCellRendererProps;
  compo: ComponentState;
  sjmoCode: string;
}) {
  const rawValue = params.value;
  const cacheKey = isEntityValue(rawValue)
    ? null
    : getCacheKey(compo.joinTableName, rawValue != null ? String(rawValue) : null);

  const [resolvedValue, setResolvedValue] = useState<AutocompleteViewEntity | unknown>(() => {
    if (isEntityValue(rawValue)) {
      return toViewEntity(rawValue);
    }
    if (cacheKey) {
      const cached = getAutocompleteCache(cacheKey);
      return cached ? toViewEntity(cached) : rawValue;
    }
    return rawValue;
  });

  useEffect(() => {
    if (isEntityValue(rawValue)) {
      setResolvedValue(toViewEntity(rawValue));
      return;
    }

    if (rawValue == null || rawValue === "" || !compo.joinTableName) {
      setResolvedValue(rawValue);
      return;
    }

    const cached = cacheKey ? getAutocompleteCache(cacheKey) : null;
    if (cached) {
      setResolvedValue(toViewEntity(cached));
      return;
    }

    const source = axios.CancelToken.source();
    const rsql = initRsqlCriteria();
    rsql.filters.and(new RSQLFilterExpression("id", Operators.Equal, rawValue));

    findView(
      {
        tableName: compo.joinTableName,
        sjmoCode,
        labelDetailsContext: compo.joinListFields,
        filter: rsql.build() + "&",
        first: 0,
        size: 1,
      },
      source,
    )
      .then((res) => {
        const selected = res?.data?.data?.[0];
        const entity = selected
          ? toViewEntity(selected)
          : toViewEntity({ id: rawValue, label: rawValue });
        if (cacheKey) {
          setAutocompleteCache(cacheKey, entity as Pojo);
        }
        setResolvedValue(entity);
      })
      .catch((e) => {
        if (!axios.isCancel(e)) {
          console.error("error during the fetch of the entity with id=", rawValue);
          console.error(e);
        }
      });

    const unregister = registerAutocompleteCallback(cacheKey, () => {
      const next = getAutocompleteCache(cacheKey);
      setResolvedValue(next ? toViewEntity(next) : rawValue);
    });

    return () => {
      source.cancel();
      unregister();
    };
  }, [rawValue, compo.joinTableName, compo.joinListFields, sjmoCode, cacheKey]);

  const displayValue = resolvedValue;

  return (
    <>
      {isEntityValue(displayValue) && displayValue.label
        ? displayValue.label
        : isEntityValue(displayValue)
          ? displayValue.id
          : params.value}
    </>
  );
}

export function buildAutocompleteViewCellRenderer(compo: ComponentState, sjmoCode: string) {
  return (params: CustomCellRendererProps) => (
    <AutocompleteViewCellRendererContent params={params} compo={compo} sjmoCode={sjmoCode} />
  );
}

export default buildAutocompleteViewCellRenderer;
